import { generateTraceId } from "./AxiosInstanceUtil";

/**
 * Builds the same headers the axios request interceptor injects
 * (see chargeAxiosInstance / documentAxiosInstance), otherwise the gateway rejects the call.
 */
export const buildAuthHeaders = (): { [key: string]: string } => ({
    "Content-Type": "application/json",
    "Authorization": `Bearer ${localStorage.getItem("access_token_with_refresh")}`,
    "BuToken": `${localStorage.getItem("access_bu_token")}`,
    "X-Trace-Id": generateTraceId(),
    "Selected-Printer": `${localStorage.getItem("CURRENT_PRINTER")}`,
});

/**
 * Sends a POST request that is able to survive the page being unloaded
 * (tab closed / browser closed / navigation away).
 *
 * Regular axios/XHR requests are cancelled by the browser as soon as the document
 * starts unloading, so a rollback call triggered on `unload` would never reach the server.
 *
 * Strategy:
 *  1. `fetch(..., { keepalive: true })` - keeps the custom headers (Authorization / BuToken)
 *     and is guaranteed to be sent by the browser even after the document is gone.
 *  2. `navigator.sendBeacon(...)` - fallback for browsers without keepalive fetch.
 *     It cannot carry custom headers, so it is best effort only.
 *
 * @returns true when the request has been handed over to the browser.
 */
export const postOnPageUnload = (baseURL: string | undefined, path: string, payload: unknown): boolean => {
    const url = `${baseURL ?? ''}${path}`;
    const body = JSON.stringify(payload ?? null);

    try {
        if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
            window.fetch(url, {
                method: "POST",
                headers: buildAuthHeaders(),
                body: body,
                keepalive: true,
            }).catch(() => undefined);
            return true;
        }
    } catch (error) {
        // ignore, fall through to sendBeacon
    }

    try {
        if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
            return navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
        }
    } catch (error) {
        // ignore
    }

    return false;
}
